import { memo, useMemo } from 'react';

import cx from 'classnames';

import { selectedQuantityPercentage } from 'actions/placeorder';
import { MARGIN_MODE } from 'constants/enums';
import { isSpotContract } from 'helpers/utils';
import { APP_DRAWER_TAB_ID_MAPPING } from 'reducers/initialstate/appDrawer';
import {
  appDrawerSelector,
  enabledPortfolioAssetsSelector,
  userMarginModeSelector,
} from 'selectors/otherSelectors';
import {
  calculatedQuantityOnDropdownChangeSelector,
  inputValueBasedOnPercentageSelector,
  selectedPercentageSelector,
  userSelectedQuantityPercentageSelector,
} from 'selectors/placeOrderSelector';
import { selectedProductSelector } from 'selectors/tradeSelectors';
import { useAppDispatch, useAppSelector, useAppSelectorOnEvent } from 'storeHooks';

import quantityChangeEpic from './quantityChange.thunk';
import styles from './QuantityInput.module.scss';

const optionList = [
  {
    label: '10%',
    value: 0.1,
  },
  {
    label: '25%',
    value: 0.25,
  },
  {
    label: '50%',
    value: 0.5,
  },
  {
    label: '75%',
    value: 0.75,
  },
  {
    label: '100%',
    value: 1,
  },
];

const Percentages = () => {
  const dispatch = useAppDispatch();
  const selectedProduct = useAppSelector(selectedProductSelector);
  const marginMode = useAppSelector(userMarginModeSelector);
  const enabledPortfolios = useAppSelector(enabledPortfolioAssetsSelector);
  const activePercent = useAppSelector(selectedPercentageSelector);
  const { activeTab } = useAppSelector(appDrawerSelector);
  const contractType = selectedProduct?.contract_type;
  const userSelectedPercentage = useAppSelector(userSelectedQuantityPercentageSelector);
  /**
   * conditions to show or hide percentages
   */
  const showPercentagesBar = useMemo(() => {
    if (activeTab === APP_DRAWER_TAB_ID_MAPPING.CHART_ORDERBOOK) {
      return false;
    }
    if (isSpotContract(contractType)) {
      return true;
    }
    switch (marginMode) {
      case MARGIN_MODE.ISOLATED:
        return true;
      case MARGIN_MODE.CROSS:
        return false;
      case MARGIN_MODE.PORTFOLIO:
        return !!enabledPortfolios?.[selectedProduct?.underlying_asset?.symbol || 0];
      default:
        return false;
    }
  }, [
    enabledPortfolios,
    marginMode,
    contractType,
    selectedProduct?.underlying_asset?.symbol,
    activeTab,
  ]);

  const calculateValueByPercent = useAppSelectorOnEvent(
    inputValueBasedOnPercentageSelector
  );

  const getCalculatedQuantity = useAppSelectorOnEvent(
    calculatedQuantityOnDropdownChangeSelector
  );

  if (!showPercentagesBar) {
    return null;
  }

  const handlePercentageClick = (val: number) => {
    dispatch(selectedQuantityPercentage(val * 100));
    const valueFromPercent = calculateValueByPercent(val);
    if (isSpotContract(contractType)) {
      dispatch(quantityChangeEpic(valueFromPercent));
    } else {
      // fill the input with the value from the percentage
      const value = getCalculatedQuantity(valueFromPercent);
      dispatch(quantityChangeEpic(value));
    }
  };

  return (
    (<div
      className={cx('flex-set flex--content-between', styles.percentagesContainer)}
      data-palette="Percentages">
      {optionList?.map?.(({ label, value }) => (
        <button
          data-testid={`quantity-percent-${label}`}
          type="button"
          className={cx(styles.percentages, {
            [styles.activePercent]:
              activePercent === label || `${userSelectedPercentage}%` === label,
          })}
          key={label}
          onClick={() => handlePercentageClick(value)}
          data-palette="Percentages">
          {label}
        </button>
      ))}
    </div>)
  );
};

export default memo(Percentages);
